import { Button, Grid } from "@mui/material";
import { IPairingCheckInStatus, IPairingCheckInStatusResponse } from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";

// Styling
import { Theme, useTheme } from "@mui/material";

interface IPairingCheckInStatusWindowProps {
  id: string;
  path: string[];
}

export enum PositionCheckInFilterType {
  ALL = "ALL",
  CHECKED_IN = "CHECKED_IN",
  NOT_CHECKED_IN = "NOT_CHECKED_IN",
  ERROR = "ERROR",
}

interface IErrorTypeOption {
  label: string;
  value: PositionCheckInFilterType;
}

const errorOptions: IErrorTypeOption[] = [
  { label: "All", value: PositionCheckInFilterType.ALL },
  { label: "Checked In", value: PositionCheckInFilterType.CHECKED_IN },
  { label: "Not Checked In", value: PositionCheckInFilterType.NOT_CHECKED_IN },
  // ?{ label: "Warning", value: PositionCheckInFilterType.WARNING },
  { label: "Error", value: PositionCheckInFilterType.ERROR },
];

const crewCheckInStyles = (theme: Theme): object => ({
  flexGrow: 1,
  margin: "1rem",
  "& .shortcode_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 50 },
    },
  },
  "& .employeeid_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 100 },
    },
  },
  "& .crewname_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 150 },
    },
  },
  "& .status_type_filter": {
    "& .input_control label": { width: "auto" },
  },
  "& .refresh_btn": {
    margin: "5px",
    padding: "2px 8px",
    height: 20,
    fontSize: 10,
    "& .refresh_icon": {
      width: "0.8em",
      height: "0.8em",
    },
  },
  "& .loadingSpinner": {
    position: "absolute",
    marginTop: "-18px",
    marginLeft: "-16px",
    height: "calc(100% - 34px)",
    width: "100%",
    zIndex: 1,
  },
});

export const tableInfo: ITableColumn[] = [
  {
    id: "id",
    value: "id",
    index: 0,
    numeric: false,
    label: "Id",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "status",
    value: "status",
    index: 1,
    numeric: false,
    label: "Status",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "pairing",
    value: "pairing",
    index: 2,
    numeric: false,
    label: "Pairing",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewNumber",
    value: "crewNumber",
    index: 3,
    numeric: false,
    label: "Employee #",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "crewMember",
    value: "crewMember",
    index: 4,
    numeric: false,
    label: "Crew",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "phonePrimary",
    value: "phonePrimary",
    index: 5,
    numeric: true,
    label: "Primary Phone #",
    type: "object",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "emailSentAt",
    value: "emailSentAt",
    index: 6,
    numeric: false,
    label: "Reminder Sent At",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "emailedCheckInTime",
    value: "emailedCheckInTime",
    index: 7,
    numeric: false,
    label: "Sent Check-In",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "pairingCheckIn",
    value: "pairingCheckIn",
    index: 8,
    numeric: false,
    label: "Actual Check-In",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "userCheckedInFor",
    value: "userCheckedInFor",
    index: 9,
    numeric: false,
    label: "Checked In For",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "userCheckedInAt",
    value: "userCheckedInAt",
    index: 10,
    numeric: false,
    label: "Response Time",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "userCheckedInBy",
    value: "userCheckedInBy",
    index: 10,
    numeric: false,
    label: "Checked In By",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "manualCheckIn",
    value: "manualCheckIn",
    index: 11,
    numeric: false,
    label: "Check In",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "sendReminder",
    value: "sendReminder",
    index: 12,
    numeric: false,
    label: "Send Reminder",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
];

export const tableDesc = {
  tableTitle: "Pairing Check-In Status",
  tableDescription: "",
  noMessage: "No Check-In Statuses found",
};

/**
 * Function returned pairing check-in status with applied filters.
 * @param rows - table rows
 * @param flightNumber - flight number filter
 * @param origin - origin filter
 * @param destination - destination filter
 */
const filterTableRows = (
  rows: IPairingCheckInStatus[],
  crewName: string,
  employeeId: string,
  shortCode: string
): IPairingCheckInStatus[] => {
  const filteredRows = rows.filter((row: IPairingCheckInStatus) => {
    const filter1 = crewName ? row.crewMember.toLowerCase().includes(crewName.toLowerCase()) : true;
    const filter2 = employeeId ? row.crewNumber.startsWith(employeeId) : true;
    const filter3 = shortCode ? row.pairing?.shortId.startsWith(shortCode.toUpperCase()) : true;
    return filter1 && filter2 && filter3;
  });
  return filteredRows;
};

/**
 * PairingCheckInStatusWindow - Retrieves the Crew summary for checkin status and displays in table.
 * If crew member is checked in for correct time it is in SUCCESS statte
 * if crew member is not checked in but email has been sent it is in WARNING state
 * if crew member has checked in for wrong time or for pairing that doesn't exist anymore it is in ERROR state
 * @param props - Contains the window id and the window path for this mosaic. Helpful to reference parent container
 */
export const PairingCheckInStatusWindow = (props: IPairingCheckInStatusWindowProps): JSX.Element => {
  const { id } = props;
  const theme: Theme = useTheme();

  const [pairingCheckInStatusData, setPairingCheckInStatusData] = useState<IPairingCheckInStatusResponse | null>(null);
  const [selectedOption, setSelectedOption] = useState<IErrorTypeOption>(errorOptions[0]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const refreshStatus = useCallback(async () => {
    setIsLoading(true);
    const response: IPairingCheckInStatusResponse = await AmplifyAPIWrapper.Get("/crew/checkInStatus");
    setPairingCheckInStatusData(response);
    setIsLoading(false);
  }, []);

  // Attempt to load check-in status upon loading component.
  useEffect(() => {
    if (refreshStatus) refreshStatus();
  }, [refreshStatus]);

  const mosaicPanel = useMemo(() => document.getElementById(`${id}-panel`), [id]);

  const tableRows = useMemo(() => {
    if (!pairingCheckInStatusData) {
      return [];
    }
    const { errorCheckIns, warningCheckIns, successfulCheckIns } = pairingCheckInStatusData;
    switch (selectedOption.value) {
      // case PositionCheckInFilterType.WARNING:
      //   return warningCheckIns;
      case PositionCheckInFilterType.ERROR:
        return errorCheckIns;
      case PositionCheckInFilterType.NOT_CHECKED_IN:
        return [...errorCheckIns, ...warningCheckIns, ...successfulCheckIns].filter(
          (value: IPairingCheckInStatus) => !value.checkIn
        );
      case PositionCheckInFilterType.CHECKED_IN:
        return [...errorCheckIns, ...warningCheckIns, ...successfulCheckIns].filter(
          (value: IPairingCheckInStatus) => value.checkIn
        );
      case PositionCheckInFilterType.ALL:
      default:
        return [...errorCheckIns, ...warningCheckIns, ...successfulCheckIns];
    }
  }, [pairingCheckInStatusData, selectedOption]);

  const [crewName, setCrewName] = useState<string>(null);
  const [employeeId, setEmployeeId] = useState<string>(null);
  const [shortCode, setShortCode] = useState<string>(null);

  const filteredTableRows = useMemo(() => {
    return filterTableRows(tableRows, crewName, employeeId, shortCode);
  }, [tableRows, crewName, employeeId, shortCode]);

  const tableData = useMemo(() => {
    return [
      {
        id: "pairingCheckInStatus",
        dataType: "PairingCheckInStatus",
        label: "Check-In Status",
        data: filteredTableRows,
        tableInfo: tableInfo,
        tableInfoDisplay: tableDesc,
      },
    ];
  }, [filteredTableRows]);

  return (
    <Grid sx={crewCheckInStyles(theme)} container direction="column">
      <SpinnerProgress
        className={"loadingSpinner"}
        loading={isLoading}
        message="Loading Check-In Data"
        fullscreen={false}
      />
      <Grid item>
        <FilterSearchTable
          id={id}
          events={tableData}
          uniqueKey={"pairingCheckIn"}
          orderDirection={"asc"}
          hideSelectionCheckboxes={true}
          hideFocusSelected={true}
          defaultRowsPerPage={10}
          selectModeSingle={true}
          hideFilter={false}
          disableRowSelect={true}
          showUTCLocalToggle={true}
          optionalControls={
            <Grid container style={{ width: "auto" }}>
              <Grid item>
                <SelectField
                  label="Filter Status"
                  className="status_type_filter"
                  name="pairing_checking_status_type"
                  menuPortalTarget={mosaicPanel}
                  menuPosition="fixed"
                  options={errorOptions}
                  value={selectedOption}
                  onChange={setSelectedOption}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filter Pairing"
                  className="shortcode_filter"
                  name="checkin_status_shortcode_filter"
                  value={shortCode}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setShortCode(value);
                  }}
                  type="text"
                  fullWidth={false}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filter Employee #"
                  className="employeeid_filter"
                  name="checkin_status_employeeid_filter"
                  value={employeeId}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setEmployeeId(value);
                  }}
                  type="text"
                  fullWidth={false}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filter Crew"
                  className="crewname_filter"
                  name="checkin_status_crewname_filter"
                  value={crewName}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setCrewName(value);
                  }}
                  type="text"
                  fullWidth={false}
                />
              </Grid>

              <Grid item>
                <Button
                  className="refresh_btn"
                  id={"refresh_checkin_status_btn"}
                  onClick={() => refreshStatus()}
                  color="primary"
                  disabled={isLoading}
                  variant="contained"
                >
                  {"Refresh"}
                </Button>
              </Grid>
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
};
